import React from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider } from "@clerk/react";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import { getClerkPublishableKey } from "./lib/clerkEntitlements";
import { currentRegionHref } from "./lib/regionLocation";
import { applySiteTheme } from "./lib/siteTheme";
import { installBackendAuthFetch } from "./lib/backendAuth";
import { installInputModality } from "./lib/inputModality";
import "./styles/theme-sheet.css";
import "./styles/theme-sheet-product.css";
import "./styles/mobile/chrome.css";
import "./styles/mobile/chooser.css";
import "./styles/mobile/sg-marketing.css";
import "./styles/mobile/us-marketing.css";
import "./styles/mobile/product.css";
// import { initErrorTracking } from "./utils/errorTracking";
import { trackPageView } from "./utils/analytics";
// import { captureException } from "./utils/errorTracking";

function injectStylesheet(href) {
  if (document.querySelector(`link[data-optra-style="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.optraStyle = href;
  document.head.appendChild(link);
}

async function loadAppStyles() {
  if (import.meta.env.DEV) {
    injectStylesheet("/dev-localhost.css");
    injectStylesheet("/accessibility.css");
    return;
  }

  await import("./styles/index.css");
  await import("./styles/accessibility.css");
}

await loadAppStyles();

// Send the Clerk session token with every backend call (verified server-side).
installBackendAuthFetch();

// No focus outlines on click or tap; keyboard users keep theirs (see lib/inputModality.js).
installInputModality();

// Optional preview theme for the regional pages (?theme=sheet). No-op unless chosen.
applySiteTheme();

// Make trackPageView available globally for RouteTracker
if (typeof window !== 'undefined') {
  window.trackPageView = trackPageView;
}

// Register service worker (with error handling)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    // Unregister any existing service workers first to prevent caching issues
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister().catch((err) => {
          console.warn('Service worker unregistration failed:', err);
        });
      }
    });
    
    // Register new service worker
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('✅ Service Worker registered:', registration);
      })
      .catch((error) => {
        console.error('❌ Service Worker registration failed:', error);
        // Don't block app if service worker fails
      });
  });
}

console.log("🚀 main.jsx loading... Environment:", import.meta.env.MODE);
console.log("📍 Base URL:", import.meta.env.BASE_URL);

try {
  const rootElement = document.getElementById("root");
  console.log("🔍 Root element:", rootElement);
  
  if (!rootElement) {
    throw new Error("Root element #root not found in DOM");
  }

  const root = ReactDOM.createRoot(rootElement);
  console.log("✅ ReactDOM.createRoot successful");
  console.log("📦 React version:", React.version);

  const appTree = (
    <ErrorBoundary reloadOnRetry={true}>
      <App />
    </ErrorBoundary>
  );

  const clerkPublishableKey = getClerkPublishableKey();
  const clerkJsUrl = (import.meta.env.VITE_CLERK_JS_URL || "").trim();

  if (clerkPublishableKey) {
    root.render(
      <React.StrictMode>
        <ClerkProvider
          publishableKey={clerkPublishableKey}
          // Clerk navigates with full URLs, outside React Router: "/" would be the region chooser.
          afterSignOutUrl={currentRegionHref("/")}
          {...(clerkJsUrl ? { clerkJSUrl: clerkJsUrl } : {})}
        >
          {appTree}
        </ClerkProvider>
      </React.StrictMode>
    );
  } else {
    root.render(
      <React.StrictMode>
        {appTree}
      </React.StrictMode>
    );
  }
  
  console.log("✅ App rendered successfully");
} catch (error) {
  console.error("❌ Application mount failed:", error);
  console.error("❌ Error stack:", error.stack);
  
  // Show detailed error fallback
  const rootElement = document.getElementById("root");
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        font-family: system-ui, sans-serif;
        text-align: center;
        color: #333;
        padding: 2rem;
        background: #f8f9fa;
      ">
        <h1 style="color: #dc3545; margin-bottom: 1rem;">⚠️ Application Error</h1>
        <p style="margin-bottom: 1rem;">The application failed to initialize.</p>
        <button onclick="window.location.reload()" style="
          padding: 0.75rem 1.5rem;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
        ">Refresh Page</button>
        <pre style="
          background: #fff;
          padding: 1rem;
          margin-top: 1rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          text-align: left;
          max-width: 600px;
          overflow-x: auto;
          font-size: 0.875rem;
        ">${error.message}\n\n${error.stack || ''}</pre>
      </div>
    `;
  }
}
